/**
 * useWlPlans — hooks de listagem e mutações de planos no painel /wl
 *
 * Todas as chamadas passam pelas RPCs `wl_list_plans` / `wl_upsert_plan` /
 * `wl_toggle_plan_active` (SECURITY DEFINER + wl_assert_permission). Mantém
 * cache compartilhado para que o filtro de plano em /wl/tenants reuse a lista.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlPlans");

export interface WlPlan {
  id: string;
  name: string;
  description: string | null;
  max_inboxes: number;
  max_agents: number;
  max_channels: number;
  max_instances: number;
  max_instagram_instances: number;
  max_crm_workspaces: number;
  max_messages_month: number | null;
  max_tasks_month: number;
  max_tasks_storage_mb: number;
  // Limites informativos (sem enforcement).
  max_pipelines: number;
  max_webhooks: number;
  max_flows: number;
  max_contacts: number;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  has_campaigns: boolean;
  // Módulos inclusos automaticamente ao contratar o plano (Fase módulos inclusos).
  has_crm: boolean;
  has_financial: boolean;
  has_tasks: boolean;
  has_referral: boolean;
  has_digital_contracts: boolean;
  // IDs do Stripe (editáveis no painel WL para conectar produto/preço no integrador financeiro).
  stripe_product_id: string | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;

  // Multi-gateway v1 (Fase 1/2) — define se o plano usa o fluxo legado Stripe
  // ou a integração V.1 lastreada em serviço de recorrência do tenant master.
  billing_integration: "legado" | "integracao_v1";
  billing_gateway_slug: string | null;
  // Meios de pagamento aceitos pelo plano — UUIDs dos meios cadastrados no
  // módulo Financeiro do tenant master (resolvidos pelo gateway selecionado).
  accepted_payment_methods: string[];
  uaizy_master_service_id: string | null;

  // Fase F — trial automático + plano padrão + ocultação no self-service.
  trial_days: number;
  is_default_for_new_tenant: boolean;
  is_hidden_from_self_service: boolean;

  // v1.7.0 — modelo de cobrança: pré-pago (libera após pagamento) ou pós-pago
  // (libera imediatamente, cobra no fechamento do ciclo).
  billing_type: "pre_paid" | "post_paid";

  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  tenants_using_count: number;
}


export interface WlPlanFilters {
  includeInactive?: boolean;
  search?: string | null;
}

export function useWlPlans(filters: WlPlanFilters = {}) {
  const includeInactive = filters.includeInactive ?? true;
  const search = (filters.search || "").trim() || null;

  return useQuery({
    queryKey: ["wl-plans", { includeInactive, search }],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WlPlan[]> => {
      const { data, error } = await supabase.rpc("wl_list_plans" as never, {
        p_include_inactive: includeInactive,
        p_search: search,
      } as never);
      if (error) {
        log.error("Erro ao listar planos", { error });
        throw error;
      }
      return ((data || []) as any[]).map((p) => ({
        ...p,
        price_monthly: Number(p.price_monthly) || 0,
        price_yearly: Number(p.price_yearly) || 0,
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
        accepted_payment_methods: Array.isArray(p.accepted_payment_methods)
          ? (p.accepted_payment_methods as string[])
          : [],
        trial_days: Number(p.trial_days) || 0,
        is_default_for_new_tenant: !!p.is_default_for_new_tenant,
        is_hidden_from_self_service: !!p.is_hidden_from_self_service,
        tenants_using_count: Number(p.tenants_using_count) || 0,
      })) as WlPlan[];
    },
  });
}


export function useWlPlanMutations() {
  const qc = useQueryClient();

  const invalidate = () => qc.invalidateQueries({ queryKey: ["wl-plans"] });

  const upsert = useMutation({
    mutationFn: async (input: { payload: Record<string, unknown>; id?: string | null }) => {
      const { data, error } = await supabase.rpc("wl_upsert_plan" as never, {
        p_payload: input.payload,
        p_id: input.id ?? null,
      } as never);
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { data, error } = await supabase.rpc("wl_toggle_plan_active" as never, {
        p_id: input.id,
        p_active: input.active,
      } as never);
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: invalidate,
  });

  return { upsert, toggleActive };
}
